import type { ViewController } from '@presentation/shell/NexxusShell';
import type { Ecriture } from '@shared/types/Ecriture';
import { EtatsFinanciersService } from '@domain/comptabilite/EtatsFinanciersService';
import { fmtFCFA } from '@presentation/shell/format';

function kpi(label: string, value: string, color?: string): string {
  return `<div class="kpi"><div class="label">${label}</div><div class="value"${color ? ` style="color:${color};"` : ''}>${value}</div></div>`;
}
function line(label: string, value: number, strong = false): string {
  const l = strong ? `<strong>${label}</strong>` : label;
  const v = strong ? `<strong>${fmtFCFA(value)}</strong>` : fmtFCFA(value);
  return `<tr><td>${l}</td><td class="num">${v}</td></tr>`;
}

export const etatsController: ViewController = async (section, ctx) => {
  const all = await ctx.ecritures.list();
  const periodes = Array.from(new Set(all.map((e) => e.periode))).sort();

  const from = section.querySelector<HTMLInputElement>('#ef-from');
  const to = section.querySelector<HTMLInputElement>('#ef-to');
  if (from && !from.value && periodes.length) from.value = periodes[0]!;
  if (to && !to.value && periodes.length) to.value = periodes[periodes.length - 1]!;

  const render = (): void => {
    const f = from?.value || (periodes[0] ?? '');
    const t = to?.value || (periodes[periodes.length - 1] ?? '');
    const rows: Ecriture[] = all.filter((e) => (!f || e.periode >= f) && (!t || e.periode <= t));

    const bilan = EtatsFinanciersService.calculerBilan(rows);
    const cr = EtatsFinanciersService.calculerCompteResultat(rows);

    const kpiGrid = section.querySelector<HTMLElement>('#ef-kpi-grid');
    if (kpiGrid) {
      kpiGrid.innerHTML =
        kpi('Total actif', fmtFCFA(bilan.total_actif)) +
        kpi('Total passif', fmtFCFA(bilan.total_passif)) +
        kpi(
          'Résultat net',
          fmtFCFA(cr.resultat_net),
          cr.resultat_net >= 0 ? 'var(--teal)' : 'var(--danger)',
        ) +
        kpi(
          'Équilibre',
          bilan.equilibre ? 'OK' : 'Déséquilibré',
          bilan.equilibre ? 'var(--success)' : 'var(--danger)',
        );
    }

    const actif = section.querySelector('#ef-bilan-actif tbody');
    if (actif) {
      actif.innerHTML =
        line('Actif immobilisé (classe 2)', bilan.actif_immobilise) +
        line('Actif circulant (classes 3-4)', bilan.actif_circulant) +
        line('Trésorerie actif (classe 5)', bilan.tresorerie_actif) +
        line('TOTAL ACTIF', bilan.total_actif, true);
    }
    const passif = section.querySelector('#ef-bilan-passif tbody');
    if (passif) {
      passif.innerHTML =
        line('Capitaux propres avant résultat (comptes 10 à 15)', bilan.capitaux_propres_hors_resultat) +
        line(
          `Résultat net de l'exercice (${bilan.compte_resultat_net}) — ${bilan.resultat_net < 0 ? 'perte' : 'bénéfice'}`,
          bilan.resultat_net,
        ) +
        line('Total capitaux propres', bilan.capitaux_propres, true) +
        line('Dettes long terme (16-18)', bilan.dettes_long_terme) +
        line('Provisions pour risques (19)', bilan.provisions_risques) +
        line('Dettes court terme', bilan.dettes_court_terme) +
        line('Trésorerie passif', bilan.tresorerie_passif) +
        line('TOTAL PASSIF', bilan.total_passif, true);
    }
    const crt = section.querySelector('#ef-cr-table tbody');
    if (crt) {
      crt.innerHTML =
        line("Produits d'exploitation", cr.produits_exploitation) +
        line("Charges d'exploitation", cr.charges_exploitation) +
        line("Résultat d'exploitation", cr.resultat_exploitation, true) +
        line('Produits financiers', cr.produits_financiers) +
        line('Charges financières', cr.charges_financieres) +
        line('Résultat financier', cr.resultat_financier, true) +
        line('Produits HAO', cr.produits_HAO) +
        line('Charges HAO', cr.charges_HAO) +
        line('Impôts sur le résultat', cr.impots) +
        line('RÉSULTAT NET', cr.resultat_net, true);
    }
    const sig = EtatsFinanciersService.calculerSIG(rows);
    const sigt = section.querySelector('#ef-sig-table tbody');
    if (sigt) {
      sigt.innerHTML =
        line('Marge commerciale', sig.marge_commerciale) +
        line("Production de l'exercice", sig.production) +
        line('Valeur ajoutée', sig.valeur_ajoutee) +
        line('Excédent brut d’exploitation (EBE)', sig.ebe) +
        line("Résultat d'exploitation", sig.resultat_exploitation, true) +
        line('Résultat financier', sig.resultat_financier) +
        line('Résultat des activités ordinaires (RAO)', sig.rao) +
        line('Résultat HAO', sig.resultat_hao) +
        line('Résultat avant IS', sig.resultat_avant_is) +
        line('Impôt sur les sociétés', sig.is) +
        line('RÉSULTAT NET', sig.resultat_net, true);
    }
  };
  render();

  const w = window as unknown as Record<string, unknown>;
  w['renderEtatsFinanciers'] = () => render();
};
